<template>
  <div />
</template>

<script>

/**
 * Vue: Boost api call (boost-api-call)
 * -----------------------------------------------------------------------------
 * Component to make Boost API calls.
 * https://help.boostcommerce.net/category/668-filter-and-search-apis-app-customization
 *
 * @param {String} buildFilterTree - Leave blank if filters aren't needed or
 * endpoint is 'search/suggest', otherwise value is '&build_filter_tree=true'.
 * @param {String} choices - Formatted filter choices to use in API call.
 * @param {Number} collectionId - Collection id to use for 'filter' endpoint.
 * @param {String} endpoint - Determines which API to use. Options are 'filter'
 * (for collections), 'search' or 'search/suggest' (predictive search).
 * @param {Number} limit - Number of products to retrieve per page.
 * @param {String} shop - Permanent shop domain.
 * @param {String} query - Search query.
 *
 */
export default {
  name: 'BoostApiCall',

  props: {
    buildFilterTree: {
      type: Boolean,
      default: true,
    },
    choices: {
      type: String,
      default: '',
    },
    collectionId: {
      type: Number,
      default: null,
    },
    endpoint: {
      type: String,
      default: '',
    },
    limit: {
      type: Number,
      default: null,
    },
    shop: {
      type: String,
      default: '',
    },
    query: {
      type: String,
      default: '',
    },
  },

  emits: {
    'boost-updated': (payload) => typeof payload === 'object',
    'boost-updated-append': (payload) => typeof payload === 'object',
    'boost-updated-prepend': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      global: true,
    }
  },

  computed: {
    shopUrl() {
      return this.shop.replace('//', '').split('/')[0]
    },
  },

  watch: {

    /**
     * Watch choices update to get product results.
     */
    choices(choices, oldChoices) {
      const newPage = choices.includes('page')
        ? Number(choices.split('&').find(
          (choice) => choice.includes('page'),
        // eslint-disable-next-line no-useless-escape
        ).replace(/[^0-9\.]+/g, ''))
        : 1

      if (oldChoices !== null && choices.includes('page')) {
        const oldPage = oldChoices.includes('page')
          ? Number(oldChoices.split('&').find(
            (choice) => choice.includes('page'),
          // eslint-disable-next-line no-useless-escape
          ).replace(/[^0-9\.]+/g, ''))
          : null

        if (newPage !== oldPage) {
          if (newPage > oldPage) {
            this.getProductResults(choices, {
              action: 'append',
              page: newPage,
            })

            return
          }

          this.getProductResults(choices, {
            action: 'prepend',
            page: newPage,
          })
        }

        return
      }

      this.getProductResults(choices, { page: newPage })
    },

    /**
     * Watch query update to get product results.
     */
    query() {
      this.getProductResults(this.choices)
    },

    /**
     * Watch query update to get product results.
     */
    endpoint() {
      this.getProductResults(this.choices)
    },
  },

  methods: {

    /**
     * Format filter tree param for API call.
     * @returns {String}
     */
    getFilters() {
      if (this.buildFilterTree === true) {
        return '&build_filter_tree=true'
      }

      return ''
    },

    /**
     * Check collection ID before making API call.
     * @returns {String}
     */
    getCollectionScope() {
      let collectionScope = `&collection_scope=${this.collectionId}`

      if (this.collectionId === null) {
        collectionScope = ''
      }

      return collectionScope
    },

    /**
     * Check products per page limit before making API call.
     * @returns {String}
     */
    getProductsPerPage() {
      let limit = `&limit=${this.limit}`

      if (isNaN(this.limit)) {
        limit = ''
      }

      return limit
    },

    /**
     * Get and format search query for search/suggest endpoint.
     * @returns {String}
     */
    getQuery() {
      let query = ''

      if ([
        'search',
        'search/collections',
        'search/pages',
        'search/suggest',
      ].includes(this.endpoint)) {
        query = `&q=${this.query}&fuzzy=2`
      }

      return query
    },

    /**
     * Get locale.
     * @returns {String}
     */
    getLocale() {
      return `&locale=${cnvs.store.language}`
    },

    /**
     * Get currency.
     * @returns {String}
     */
    getCurrency() {
      return `&currency=${cnvs.store.currency}`
    },

    /**
     * Handle API call to Boost.
     * @param {String} choices - Search parameters to add to request url.
     * @param {Object} [paginationSettings] - Contains information
     * related to page number and action.
     */
    async getProductResults(choices, paginationSettings = {}) {
      try {
        const buildFilterTree = this.getFilters()
        const collectionScope = this.getCollectionScope()
        const limit = this.getProductsPerPage()
        const query = this.getQuery()
        const { action = '', page } = paginationSettings
        let formattedChoices = choices

        if (this.endpoint === 'search/suggest' && this.query === '') {
          return
        }

        if (!choices) {
          formattedChoices = ''
        }

        let url = `https://services.mybcapps.com/bc-sf-filter/${this.endpoint}?shop=${this.shopUrl}&return_all_currency_fields=false`

        url += buildFilterTree
        url += collectionScope
        url += limit
        url += query
        url += formattedChoices
        url += this.getLocale()
        url += this.getCurrency()

        const response = await fetch(url)
        const data = await response.json()

        if (action === 'append') {
          this.$emit('boost-updated-append', {
            data,
            page,
          })
          return
        }

        if (action === 'prepend') {
          this.$emit('boost-updated-prepend', {
            data,
            page,
          })
          return
        }

        this.$emit('boost-updated', {
          data,
          page,
        })

      } catch (error) {
        throw new Error(error)
      }
    },
  },
}
</script>
